import { getDocument, isServer } from '@/lib/utils';
import { CROP_TOLERANCE_PX } from './constants';

interface TransformedImageResult {
  dataUrl: string;
  width: number;
  height: number;
}

/**
 * Normalizes a rotation angle to the -180 to 180 degree range.
 */
function normalizeRotation(rotation: number): number {
  let normalized = rotation % 360;
  if (normalized > 180) normalized -= 360;
  if (normalized < -180) normalized += 360;
  return normalized;
}

/** Max length of the longest side for the preview data URL; keeps rotate/flip responsive. */
const MAX_PREVIEW_DIMENSION = 1200;

/**
 * Creates a canvas with the image transformed (rotated and flipped).
 * Returns a data URL of the transformed image and its new dimensions.
 * Preview is capped at MAX_PREVIEW_DIMENSION for faster encoding; dimensions returned
 * are always the logical full size for correct crop scaling on save.
 *
 * The transformation order (rotate → flip) matches the backend Vips processing.
 */
export function createTransformedImage(
  img: HTMLImageElement,
  rotation: number,
  flipH: boolean,
  flipV: boolean,
): TransformedImageResult {
  if (isServer) {
    return { dataUrl: img.src, width: img.naturalWidth, height: img.naturalHeight };
  }

  const doc = getDocument();
  if (!doc) {
    return { dataUrl: img.src, width: img.naturalWidth, height: img.naturalHeight };
  }

  const canvas = doc.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { dataUrl: img.src, width: img.naturalWidth, height: img.naturalHeight };
  }

  const { naturalWidth: w, naturalHeight: h } = img;
  const normalizedRotation = normalizeRotation(rotation);
  const radians = (normalizedRotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));

  // Bounding box expands to fit the rotated image (logical full size)
  const canvasW = Math.ceil(w * cos + h * sin);
  const canvasH = Math.ceil(w * sin + h * cos);

  canvas.width = canvasW;
  canvas.height = canvasH;

  // Pre-fill with white for corner gaps on non-90° rotations (matches backend Vips processing)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasW, canvasH);

  // Canvas transforms apply in reverse order: translate → scale(flip) → rotate → draw
  ctx.translate(canvasW / 2, canvasH / 2);
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  ctx.rotate(radians);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);

  // For large images, encode a downscaled preview so toDataURL doesn't block the main thread
  const maxDim = Math.max(canvasW, canvasH);
  let outputCanvas = canvas;
  if (maxDim > MAX_PREVIEW_DIMENSION) {
    const scale = MAX_PREVIEW_DIMENSION / maxDim;
    const preview = doc.createElement('canvas');
    preview.width = Math.round(canvasW * scale);
    preview.height = Math.round(canvasH * scale);
    const previewCtx = preview.getContext('2d');
    if (previewCtx) {
      previewCtx.drawImage(canvas, 0, 0, canvasW, canvasH, 0, 0, preview.width, preview.height);
      outputCanvas = preview;
    }
  }
  const dataUrl = outputCanvas.toDataURL('image/jpeg', 0.9);

  return {
    dataUrl,
    width: canvasW,
    height: canvasH,
  };
}

/**
 * Scales crop coordinates from displayed dimensions to natural dimensions.
 * Clamps to image bounds so rounding never produces an invalid extract area (e.g. w=4001 on 4000px image).
 */
export function scaleCropToNatural(
  crop: { x: number; y: number; width: number; height: number },
  displayedWidth: number,
  displayedHeight: number,
  naturalWidth: number,
  naturalHeight: number,
): { x: number; y: number; width: number; height: number } {
  const scaleX = naturalWidth / displayedWidth;
  const scaleY = naturalHeight / displayedHeight;

  let x = Math.round(crop.x * scaleX);
  let y = Math.round(crop.y * scaleY);
  let width = Math.round(crop.width * scaleX);
  let height = Math.round(crop.height * scaleY);

  x = Math.max(0, Math.min(x, naturalWidth - 1));
  y = Math.max(0, Math.min(y, naturalHeight - 1));
  width = Math.max(1, Math.min(width, naturalWidth - x));
  height = Math.max(1, Math.min(height, naturalHeight - y));

  return { x, y, width, height };
}

/**
 * Returns true if the pixel crop effectively covers the full image (within tolerance).
 * Used to decide between "Original" vs custom when syncing aspect-ratio selection.
 */
export function coversFullImage(
  crop: { x: number; y: number; width: number; height: number },
  imageWidth: number,
  imageHeight: number,
  tolerance = CROP_TOLERANCE_PX,
): boolean {
  return (
    crop.x <= tolerance &&
    crop.y <= tolerance &&
    Math.abs(crop.width - imageWidth) <= tolerance &&
    Math.abs(crop.height - imageHeight) <= tolerance
  );
}
